import React, { useRef, Fragment, useEffect } from 'react'
import { useMergedState, useAbortRequestsOnDidMount } from 'hooks'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import { ReactComponent as DragFileIcon } from 'assets/icons/upload.svg'
import Request from 'api'
import ErrorIcon from '@material-ui/icons/Error'
import InputWrapper from './InputWrapper'
import FilesPreviewAndDrop from './FilesPreviewAndDrop'
import Button from 'components/Button'

const styles = makeStyles(theme => ({
  input: {
    width: `100%`,
    height: `100%`,
    opacity: 0,
    position: `absolute`,
    cursor: `pointer`,
    zIndex: 21,
  },
  error: {
    textAlign: `center`,
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
    width: `100%`,
  },
  container: {
    position: `relative`,
    background: `#FAFCFD`,
    borderRadius: 5,
    padding: theme.spacing(2, 3),
    overflow: `hidden`,
  },
  withHover: {
    '&:hover > svg, &:hover > div': {
      opacity: 0.6,
    },
    '&:hover svg': {
      opacity: 1,
    },
  },
  errorContainer: {
    border: `1px solid ${theme.palette.error.main}`,
  },
  icon: {
    width: 90,
    height: `auto`,
    transition: `0.3s`,
  },
  initMessage: {
    transition: `0.3s`,
    paddingTop: theme.spacing(1.5),
    width: `60%`,
    fontWeight: theme.fontWeight[0],
    color: `#8F9BB3`,
  },
  hightligthed: {
    border: `3px dashed ${theme.palette.success.main}`,
  },
  messageIcon: {
    fill: `#C5D0DE`,
    marginRight: theme.spacing(1),
  },
  messageContainer: {
    paddingTop: theme.spacing(1),
  },
  messsage: {
    color: `#525E6D`,
  },
  disabled: {
    border: `none`,
    cursor: `default`,
    '& > input': {
      cursor: `default`,
    },
    '&:hover > svg, &:hover > div': {
      opacity: 1,
    },
  },
  textHazClick: {
    color: `#007BFF`,
  },
}))

export default function FileUpload({
  id,
  label,
  error, //Puede ser boolean o string
  isRequired = false,
  onChange,
  value,
  rawValue = {},
  id: name,
  autocompleteData,
  max,
  inputType = `file`,
  acceptTypes = `image/*`,
  fileTypes = [`image/jpeg`, `image/jpg`, `image/png`],
  disabled = false,
  align = `left`,
  fullWidth = false,
  maxMBFileSize = 10,
  xs = 12,
  md = 12,
  apiEndpoint = `uploadImage`,
  message = ``,
  ...rest
}) {
  const classes = styles()
  const [{ filesLoaded, errorMessage, onDropHover, uploaded }, setState] = useMergedState({
    filesLoaded: null,
    uploaded: null,
    errorMessage: error,
    onDropHover: false,
  })
  const hiddenInput = useRef()
  const endpoint = useRef({})
  useAbortRequestsOnDidMount(
    Object.keys(endpoint.current).map(reqName => endpoint.current[reqName]),
  )

  useEffect(() => {
    if (Object.keys(rawValue).length > 0) {
      setState({
        ...rawValue,
      })
    }
    // eslint-disable-next-line
  }, [rawValue])

  function validTypes(file) {
    const { type } = file
    const isValidType = fileTypes.find(validType => validType === type)
    if (!isValidType) return false
    return true
  }

  function hasValidSize(file) {
    if (file.size / 1024 / 1024 > maxMBFileSize) return false
    return true
  }

  function isValidFileSizeAndType(file) {
    if (!file) {
      setState({ errorMessage: `No se ha seleccionado ningún archivo` })
      return false
    }
    if (!hasValidSize(file)) {
      setState({ errorMessage: `El archivo no debe pesar más de ${maxMBFileSize}MB` })
      return false
    }
    if (!validTypes(file)) {
      setState({ errorMessage: `Tipo de archivo inválido` })
      return false
    }
    return true
  }

  async function deleteNoUsedFiles() {
    const { id } = uploaded
    const deleteRequest = new Request({
      endpoint: `deleteImage`,
    })
    await deleteRequest.send(id).finally(() => setState({ uploaded: null }))
  }

  async function loadFile({ imageBlob }) {
    const {
      file: { type },
      loading,
      name: fileName,
    } = filesLoaded
    if (!loading) {
      setState({
        filesLoaded: {
          ...filesLoaded,
          loading: true,
          success: false,
          error: null,
        },
      })
      // create data and append file
      const data = new FormData()
      // creating new file instance from image cropped
      data.append(`image`, new File([imageBlob], fileName, { type }))
      // create request by fileName
      endpoint.current = {
        ...endpoint.current,
        [fileName]: new Request({
          endpoint: apiEndpoint,
          data,
        }),
      }
      if (uploaded) {
        await deleteNoUsedFiles()
      }
      // use it and send the request
      endpoint.current[fileName]
        .send()
        .then(({ data: { id, originalname } }) => {
          const nextuploaded = {
            id,
            originalname,
            success: `El archivo ${originalname}, ha sido cargado`,
          }
          setState({
            filesLoaded: null,
            uploaded: nextuploaded,
          })
          onChange({
            props: {
              error: false,
            },
            value: id,
            rawValue: {
              uploaded: nextuploaded,
            },
            name,
          })
        })
        .catch(({ message }) => {
          setState({
            filesLoaded: {
              ...filesLoaded,
              loading: false,
              success: false,
              error: message,
            },
          })
        })
    }
  }

  function readFilesAndMakeThumbnails(file) {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onloadend = function() {
      const filesLoaded = {
        type: file.type,
        src: reader.result,
        file,
        name: file.name,
        error: false,
        success: false,
        loading: false,
      }
      setState({
        filesLoaded,
      })
      onChange({
        value,
        props: {
          error: false,
        },
        rawValue: {
          filesLoaded,
          uploaded,
        },
        name,
      })
    }
  }

  function handleUploadClick({ target: { files } }) {
    if (!disabled && isValidFileSizeAndType(files[0])) {
      readFilesAndMakeThumbnails(files[0])
    }
  }

  function handleDragStatus(isEnter) {
    return () => {
      setState({ onDropHover: isEnter })
    }
  }

  function deleteFile() {
    onChange({
      props: {
        error: false,
      },
      value:null,
      rawValue: {
        filesLoaded: null,
      },
      name,
    })
    setState({ filesLoaded: null, onDropHover: false })
    console.log({
      name: name,
      value: null,
      id
    });
    
  }

  return (
    
    <InputWrapper
      label={`${label} (máx ${maxMBFileSize}MB)`}
      isRequired={isRequired}
      align={align}
      type={inputType}
      error={errorMessage}
      fullWidth={fullWidth}
      xs={xs}
      md={md}
      disabled={disabled}
      {...rest}
    >
      <Grid
        container
        className={`${classes.container} ${errorMessage ? classes.errorContainer : ``}  ${
          filesLoaded || uploaded || onDropHover ? classes.hightligthed : ``
        } ${disabled ? classes.disabled : ``} ${!filesLoaded ? classes.withHover : ``}`}
        alignItems="center"
        justify="center"
      >
        {!filesLoaded && (
          <input
            accept={acceptTypes}
            className={classes.input}
            id={`${name}-upload`}
            name="file"
            multiple={false}
            onChange={handleUploadClick}
            ref={hiddenInput}
            onDragEnter={handleDragStatus(true)}
            onDragLeave={handleDragStatus(false)}
            draggable
            type="file"
            disabled={disabled}
            autoFocus={false}
          />
        )}
        {filesLoaded || uploaded ? (
          
          <FilesPreviewAndDrop
            files={filesLoaded}
            disabled={disabled}
            onDeleteFile={deleteFile}
            onAccept={loadFile}
            fileToSHow={uploaded}
            {...rest}
          />
          
         
        ) : (
          <Fragment>
            <DragFileIcon className={classes.icon} />
            <Grid container justify="center">
              <Typography
                className={classes.initMessage}
                variant="body1"
                align="center"
                component="p"
              >
                Arrastra tu archivo o <span className={classes.textHazClick}>haz click</span> para
                adjuntar
              </Typography>
            </Grid>
          </Fragment>
        )}
      </Grid>
      {message && (
        <Grid container alignItems="center" className={classes.messageContainer}>
          <ErrorIcon className={classes.messageIcon} />
          <Typography variant="body2" className={classes.message}>
            {message}
          </Typography>
        </Grid>
      )}
      {errorMessage && (
        <Typography variant="body1" align="center" component="p" className={classes.error}>
          {errorMessage}
        </Typography>
      )}

      {filesLoaded || uploaded ? 
        <Button
          variant="contained"
          style={{backgroundColor:'red',color:'white',marginTop:10}}
          size="small"
          onClick={deleteFile}
        >
          Eliminar
        </Button>
      : <></>}
       
    </InputWrapper>
   
  )
}
